import React, { createContext, ReactNode, useContext, useEffect, useState } from "react";
import { TrackingContextFields } from "../../models/MatchAdSearchResponse.js";
import { Recommendation, RecommendationResponse, SearchParams } from "../../types.js";
import { retryFetch } from "../utils/retryFetch.js";

type RecommendationsContextType = {
	items: Recommendation[];
	fetchMore: () => Promise<void>;
	fetchingInitial: boolean;
	fetchingMore: boolean;
	type: string | undefined;
	isPersonal: boolean;
};

const RecommendationsContext = createContext<RecommendationsContextType>({
	items: [],
	fetchingInitial: true,
	fetchingMore: false,
	fetchMore: async () => {},
	type: undefined,
	isPersonal: false,
});

export const useRecommendations = () => {
	return useContext(RecommendationsContext);
};

export const RecommendationsProvider = ({
	parameters,
	proxyUrl,
	children,
}: {
	parameters: SearchParams;
	proxyUrl: string;
	children: ReactNode;
}) => {
	const [fetchingInitial, setFetchingInitial] = useState(true);
	const [fetchingMore, setFetchingMore] = useState(false);
	const [recommendations, setRecommendations] = useState<{
		items: Recommendation[];
		fetchMore: string[] | undefined;
		isPersonal: boolean;
	}>({
		items: [],
		fetchMore: undefined,
		isPersonal: false,
	});

	const fetchMore = async () => {
		const { fetchMore } = recommendations;
		if (fetchMore && fetchMore.length > 0) {
			const [item, ...rest] = fetchMore;
			setFetchingMore(true);
			const response = await fetchMoreRecommendations({
				proxyUrl,
				fetchMore: item,
				parameters,
			});
			setRecommendations({
				items: [...recommendations.items, ...response.items],
				fetchMore: rest,
				isPersonal: response.isPersonal,
			});
			setFetchingMore(false);
		}
	};

	useEffect(() => {
		const initialFetch = async () => {
			if (proxyUrl) {
				const response = await fetchRecommendations({
					proxyUrl,
					searchParams: parameters,
				});
				setRecommendations(response);
				setFetchingInitial(false);
			}
		};
		initialFetch();
	}, []);

	return (
		<RecommendationsContext.Provider
			value={{
				items: recommendations.items,
				fetchMore,
				fetchingInitial,
				fetchingMore,
				type: parameters.type,
				isPersonal: recommendations.isPersonal,
			}}
		>
			{children}
		</RecommendationsContext.Provider>
	);
};

const fetchRecommendations = async ({
	searchParams,
	proxyUrl,
	rows,
	render,
}: {
	searchParams: SearchParams;
	proxyUrl: string;
	rows?: string;
	render?: string;
}): Promise<RecommendationResponse> => {
	const parameters = new URLSearchParams(searchParams);
	if (rows) parameters.append("rows", rows);
	if (render) parameters.append("render", render);

	const url = `${proxyUrl}/recommendations?${parameters.toString()}`;

	try {
		const response = await retryFetch(url);
		const data: RecommendationResponse = await response.json();
		return mapResponse(data);
	} catch (e) {
		console.error(e);
		return {
			items: [],
			"tracking-context": {},
			fetchMore: undefined,
			hasConsent: false,
			isPersonal: false,
		};
	}
};

const fetchMoreRecommendations = async ({
	proxyUrl,
	fetchMore,
	parameters,
}: {
	proxyUrl: string;
	fetchMore: string;
	parameters: SearchParams;
}): Promise<RecommendationResponse> => {
	const searchParams = new URLSearchParams({ fetchMore, ...parameters });

	const url = `${proxyUrl}/more-recommendations?${searchParams.toString()}`;

	try {
		const response = await retryFetch(url);
		const data: RecommendationResponse = await response.json();
		return mapResponse(data);
	} catch (e) {
		console.error(e);
		return {
			items: [],
			"tracking-context": {},
			fetchMore: undefined,
			hasConsent: false,
			isPersonal: false,
		};
	}
};

const mapResponse = (data: RecommendationResponse): RecommendationResponse => {
	const trackingContexts = Object.entries(data["tracking-context"] ?? {});

	const recommendations: RecommendationResponse = {
		items: data.items.map((item) => {
			return {
				...item,
				tracking: {
					...item.tracking,
					pulse: item.tracking?.pulse
						? {
								...item.tracking.pulse,
								recommendation: item.tracking.pulse?.recommendation.map((event) => ({
									...event,
									...trackingContexts.reduce(
										(acc, [key, value]) => {
											acc[key] = value;
											return acc;
										},
										{} as Record<string, TrackingContextFields>,
									),
								})),
							}
						: undefined,
				},
			};
		}),
		"tracking-context": data["tracking-context"],
		fetchMore: data.fetchMore,
		hasConsent: data.hasConsent,
		isPersonal: data.isPersonal,
	};
	return recommendations;
};
